import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Shield, CheckCircle, Loader2, XCircle, Clock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking, success, failed, pending
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }

    const pollStatus = async () => {
      const maxAttempts = 10;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus("pending");
        return;
      }

      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${BACKEND_URL}/api/payment/status/${sessionId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.payment_status === "paid") {
          setStatus("success");
          return;
        } else if (response.data.status === "expired") {
          setStatus("failed");
          return;
        }

        // Continue polling
        setAttempts(prev => prev + 1);
        setTimeout(pollStatus, pollInterval);
      } catch (error) {
        console.error("Payment status check error:", error);
        setAttempts(prev => prev + 1);
        setTimeout(pollStatus, pollInterval);
      }
    };

    pollStatus();
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-8 pb-8 text-center">
          {/* Logo */}
          <div className="mx-auto mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_protect-workers/artifacts/awekt278_image.png" 
              alt="MyShield Logo" 
              className="h-20 w-auto mx-auto"
            />
          </div>

          {status === "checking" && (
            <>
              <Loader2 className="w-16 h-16 text-blue-500 animate-spin mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Processing Payment</h1>
              <p className="text-slate-600">Please wait while we verify your payment...</p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-12 h-12 text-emerald-500" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Payment Successful!</h1>
              <p className="text-slate-600 mb-6">
                Thank you for your payment. Your MyShield membership is now active!
              </p>
              <Button 
                onClick={async () => {
                  // Refresh user data from server
                  try {
                    const token = localStorage.getItem("token");
                    const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    localStorage.setItem("user", JSON.stringify(response.data));
                    window.location.href = "/dashboard";
                  } catch (error) {
                    window.location.href = "/login";
                  }
                }} 
                className="w-full bg-slate-900 hover:bg-slate-800"
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard
              </Button>
            </>
          )}

          {status === "pending" && (
            <>
              <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-12 h-12 text-amber-500" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Payment Processing</h1>
              <p className="text-slate-600 mb-6">
                Your payment is being verified. Please wait a moment...
              </p>
              <Button 
                onClick={async () => {
                  // Try to refresh user data
                  try {
                    const token = localStorage.getItem("token");
                    const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    localStorage.setItem("user", JSON.stringify(response.data));
                    window.location.href = "/dashboard";
                  } catch (error) {
                    window.location.href = "/login";
                  }
                }} 
                className="w-full bg-slate-900 hover:bg-slate-800"
              >
                Continue to Dashboard
              </Button>
            </>
          )}

          {status === "failed" && (
            <>
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-12 h-12 text-red-500" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Payment Failed</h1>
              <p className="text-slate-600 mb-6">
                Unfortunately, your payment could not be processed. Please try again.
              </p>
              <div className="space-y-3">
                <Button 
                  onClick={() => navigate("/payment")} 
                  className="w-full bg-slate-900 hover:bg-slate-800"
                >
                  Try Again
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate("/dashboard")} 
                  className="w-full"
                >
                  Go to Dashboard
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
